import React from 'react';
import styled from 'styled-components';
import Theme from '@grnhse/seedling/lib/birch/components/call_out/theme';
import type { CallOutProps } from '@grnhse/seedling/lib/birch/components/call_out';
import { Color } from '@grnhse/seedling/lib/birch/colors';
import { Body } from '@grnhse/seedling/lib/birch/typography';
import useDismissable from '@grnhse/seedling/lib/birch/hooks/use_dismissable';
import { Grid } from '@grnhse/seedling/lib/birch/layouts/grid';
import { Link } from '@grnhse/seedling/lib/birch/typography';
import CloseIcon from '@grnhse/seedling/lib/birch/icons/close';
import type { ButtonProps } from '@grnhse/seedling/lib/birch/components/buttons';
import { Stack } from '@grnhse/seedling/lib/birch/layouts/stack';
import {
  DangerButton,
  SecondaryButton,
} from '@grnhse/seedling/lib/birch/components/buttons';
import { ThemeProps } from '@grnhse/seedling/lib/birch/themes';
import { getDataProvidesProps } from '@grnhse/seedling/lib/birch/utils/constants';
import AiMagicIcon from '@grnhse/seedling/lib/birch/icons/ai_magic';
import { FullPalette } from '@grnhse/seedling/lib/birch/colors';

type AbstractCallOutProps = CallOutProps & {
  icon: React.ReactNode;
  backgroundColor: Color;
};

const CallOutWrapper = styled.div<{ $backgroundColor: string }>`
  background: ${({ $backgroundColor }) => $backgroundColor};
  border-radius: 5px;
  padding: 1rem 1.5rem;
`;

const IconWrapper = styled.div<ThemeProps>`
  > svg.seedling-birch-icon {
    height: ${(p) => p.theme.fontMediumLineHeight};
    width: ${(p) => p.theme.fontMediumLineHeight};
  }
`;

type CallToActionProps = ButtonProps & {
  buttonType?: 'secondary' | 'danger' | null;
};

const DismissButton = styled.button`
  background: none;
  border: none;
  border-radius: 50%;
  cursor: pointer;
  padding: 0;
  height: 1.5rem;
  width: 1.5rem;
`;

const buildButton = (props: CallToActionProps, key: string | number) => {
  if (props.buttonType === 'danger') {
    return <DangerButton {...props} key={key} />;
  } else {
    return <SecondaryButton {...props} key={key} />;
  }
};

const CallOut = (props: AbstractCallOutProps) => {
  const [dismissed, setAsDismissed] = useDismissable(
    'callOut',
    props.dismissableKey
  );

  const mainContent = (
    <>
      {props.headerText && (
        <Body medium data-provides="call-out--header-text">
          {props.headerText}
        </Body>
      )}

      {typeof props.bodyText === 'string' ? (
        <Body data-provides="call-out--body-text">{props.bodyText}</Body>
      ) : (
        props.bodyText
      )}

      {props.linkProps && <Link {...props.linkProps} />}
    </>
  );

  const buildCallToAction = (
    buttonProps: CallToActionProps | CallToActionProps[] | undefined
  ) => {
    if (!buttonProps) {
      return null;
    }

    if (Array.isArray(buttonProps)) {
      return (
        <Stack direction="row">
          {buttonProps.map((p, index) => buildButton(p, index))}
        </Stack>
      );
    }

    return buildButton(buttonProps, 1);
  };

  const callToAction = buildCallToAction(props.buttonProps);

  const closeIcon = (
    <DismissButton data-provides="call-out--dismiss" onClick={setAsDismissed}>
      <CloseIcon color={Theme.default.closeIconColor} />
    </DismissButton>
  );

  if (dismissed) {
    return <></>;
  }

  return (
    <Grid container {...getDataProvidesProps(props)}>
      <Grid item xs>
        <CallOutWrapper $backgroundColor={props.backgroundColor}>
          <Grid container spacing={1}>
            <Grid item>
              <IconWrapper>{props.icon}</IconWrapper>
            </Grid>
            <Grid item xs>
              <Grid container justifyContent="space-between">
                <Grid item>{mainContent}</Grid>
                {callToAction && <Grid item>{callToAction}</Grid>}
              </Grid>
            </Grid>
            {props.dismissableKey && <Grid item>{closeIcon}</Grid>}
          </Grid>
        </CallOutWrapper>
      </Grid>
    </Grid>
  );
};

const AiCallout = (props: CallOutProps) => {
  return (
    <CallOut
      {...props}
      icon={<AiMagicIcon />}
      backgroundColor={FullPalette.VIOLET_E25}
    />
  );
};

export default AiCallout;
